/**
 * HomePage — vibe-first landing (plan 2026-07-07 §1-2).
 *
 * The hero keeps the Texas panorama, but leads with the value ("Tell us the
 * life you want.") rather than the wordmark the nav already carries, and the
 * search experience starts immediately: three metro pills (one per city,
 * default-selected so submit always works) sit just above a large open vibe
 * textbox. First submit navigates to /vibe carrying the
 * typed text as the opening message. Criteria and Map remain available but
 * demoted to small links under the box (ADR-0001: all three modes feed one
 * Search; this page changes visual emphasis, not architecture).
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { NavigationBar } from '../organisms/NavigationBar';
import { MobileBottomNav } from '../organisms/MobileBottomNav';
import { useMetroStore } from '../../stores/metroStore';
import { useTracking } from '../../hooks/useTracking';
import { CITY_ROWS, DEFAULT_METRO } from '../../config/cityRows';
import { getIntakeUrl } from '../../config/contact';
import { Icon } from '../atoms/Icon';

const ArrowRight = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M3 8h9M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Metro-specific example query for the hero textbox placeholder. Keeps the
 *  same shape (2BR · budget · walkable neighborhood · dog-friendly · coffee)
 *  but swaps in a real neighborhood for the selected city so the prompt reads
 *  local. Falls back to Austin before a city is picked. */
const HERO_EXAMPLES: Record<string, string> = {
  Austin: '“2BR under $2,300, walkable East Austin, dog-friendly, near good coffee”',
  Dallas: '“2BR under $2,300, walkable Uptown Dallas, dog-friendly, near good coffee”',
  Houston: '“2BR under $2,300, walkable Montrose, dog-friendly, near good coffee”',
};

/** Compact metro pills — one per city, selection is a single tap. The
 *  remembered metro (metroStore persists) renders pre-selected on load. */
function MetroPills() {
  const { metros, selectedMetro, setSelectedMetro } = useMetroStore();
  const { track } = useTracking();
  const activeKeys = new Set((metros || []).map((m) => m.key));
  const rows = CITY_ROWS.filter((c) => activeKeys.size === 0 || activeKeys.has(c.key));
  // T12 — no city gate. Before the user picks, the default metro reads as
  // selected, so the hero always submits somewhere real.
  const effectiveMetro = selectedMetro || DEFAULT_METRO;
  return (
    <div
      className="flex flex-wrap justify-center gap-2"
      data-testid="home-metro-pills"
    >
      {rows.map((city) => {
        const selected = effectiveMetro === city.key;
        return (
          <button
            key={city.key}
            onClick={() => {
              setSelectedMetro(city.key);
              track('metro_select', 'home', { metro: city.key, label: city.key });
            }}
            data-testid={`home-metro-${city.key.toLowerCase()}`}
            aria-pressed={selected}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 min-h-[44px] font-semibold text-sm transition-all active:scale-[0.97]"
            style={{
              fontFamily: 'var(--ts-font-body)',
              background: selected ? city.accent : 'rgba(10,10,16,0.55)',
              color: selected ? '#141821' : 'rgba(255,255,255,0.92)',
              border: selected ? `1px solid ${city.accent}` : `1px solid ${city.accent}66`,
              boxShadow: selected ? `0 0 22px -4px ${city.glow}` : 'none',
              backdropFilter: 'blur(6px)',
            }}
          >
            <span aria-hidden>{city.emoji}</span>
            {city.key === 'Dallas' ? 'Dallas / Fort Worth' : city.key}
          </button>
        );
      })}
    </div>
  );
}

/** One step of the three-phase locating flow (Discovery → Price Report →
 *  Tours, CONTEXT.md). Board-material card + HUD-voice step number keep the
 *  section inside the neon design language instead of a flat text slab. */
function StepCard({ step, icon, emoji, title, body }: { step: string; icon?: string; emoji?: string; title: string; body: string }) {
  return (
    <div className="ts-board-card flex flex-col gap-2 p-5 text-left">
      <div className="flex items-center justify-between">
        <span
          style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,210,77,0.85)', textShadow: '0 0 10px rgba(255,200,60,0.35)' }}
          aria-hidden
        >
          {step}
        </span>
        {icon
          ? <Icon name={icon} size="md" aria-hidden style={{ color: 'var(--ts-accent-primary)' }} />
          : <span className="text-xl" aria-hidden>{emoji}</span>}
      </div>
      <span style={{ font: '800 17px var(--ts-font-display)', color: '#fff', letterSpacing: '-0.01em' }}>{title}</span>
      <span className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.62)' }}>{body}</span>
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { selectedMetro } = useMetroStore();
  const { track } = useTracking();
  const setSelectedMetro = useMetroStore((s) => s.setSelectedMetro);
  const [vibeText, setVibeText] = React.useState('');

  // T12 — default-select the launch metro instead of gating submit behind a
  // city pick. The pills remain a one-tap override; the old pulse + "Pick
  // your city first" failure path is gone.
  React.useEffect(() => {
    if (!selectedMetro) setSelectedMetro(DEFAULT_METRO);
  }, [selectedMetro, setSelectedMetro]);

  const submitVibe = () => {
    const text = vibeText.trim();
    if (!text) {
      navigate('/vibe');
      return;
    }
    track('vibe_message', 'landing', { message_length: text.length });
    // VibeSearchPage picks initialMessage off router state and sends it as
    // the opening message (vibeStore persists the conversation from there).
    navigate('/vibe', { state: { initialMessage: text } });
  };

  const goDemoted = (target: string) => {
    navigate(target);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--ts-bg-base)' }} data-testid="home-page">

      {/* ── Hero — full-bleed Texas panorama, search starts here ── */}
      <section className="relative overflow-hidden flex flex-col md:min-h-[min(800px,100svh)]">
        {/* Background image */}
        <picture>
          <source media="(max-width: 767px)" srcSet="/hero-neon-mobile.avif" type="image/avif" />
          <source srcSet="/hero-neon.avif" type="image/avif" />
          <img
            src="/hero-neon.webp"
            width={1440}
            height={720}
            alt="Neon Texas cityscape at dusk: Austin, Dallas, and Houston"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: 'center 42%' }}
            fetchPriority="high"
          />
        </picture>
        {/* Near-black gradient — subtle at top for nav, heavy at bottom to blend into the dark app */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{ background: 'linear-gradient(180deg, rgba(10,10,16,0.45) 0%, rgba(10,10,16,0.18) 32%, rgba(10,10,16,0.30) 50%, rgba(10,10,16,0.78) 74%, rgba(10,10,16,0.99) 100%)' }}
        />
        {/* Center-band scrim — keeps the centered hero text legible over the busy scene. */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{ background: 'linear-gradient(180deg, rgba(10,10,16,0) 20%, rgba(10,10,16,0.5) 52%, rgba(10,10,16,0.1) 82%)' }}
        />
        {/* Gold neon glow bloom behind the vibe box */}
        <div
          aria-hidden
          className="absolute left-1/2 -translate-x-1/2 bottom-[-10%] w-[1000px] max-w-full h-[560px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at center, var(--ts-accent-glow) 0%, transparent 70%)' }}
        />

        {/* Floating nav */}
        <NavigationBar currentPage="home" transparent />

        {/* Hero content — brand, then the search itself. The transparent nav
            is position:absolute over this section, so the stack needs real
            top clearance (pt-24) on mobile or the eyebrow crowds under it. */}
        <div className="relative flex-1 flex flex-col justify-start md:justify-center items-center text-center mx-auto w-full px-5 md:px-14 pt-24 pb-12 md:py-12 gap-4 md:gap-5 max-w-3xl">
          {/* T12 — the hero sells the value, not the brand: the nav already
              owns the wordmark, so repeating it here burned the first
              viewport's biggest type on a name the user just read. */}
          <h1
            style={{
              fontFamily: 'var(--ts-font-display)',
              margin: 0,
              fontSize: 'clamp(34px, 5.4vw, 64px)',
              fontWeight: 800,
              color: '#fff',
              lineHeight: 1.08,
              letterSpacing: '-0.03em',
              textShadow: '0 2px 20px rgba(20,24,33,0.55), 0 1px 4px rgba(20,24,33,0.7)',
            }}
          >
            Tell us the life you want.
          </h1>
          <p style={{ fontFamily: 'var(--ts-font-body)', fontSize: 'clamp(14px, 1.7vw, 18px)', fontWeight: 600, color: '#fff', lineHeight: 1.5, margin: 0, textShadow: '0 1px 12px rgba(20,24,33,0.7), 0 1px 3px rgba(20,24,33,0.8)' }}>
            We'll find the apartments that fit it — and pull real pricing on your favorites.
          </p>

          {/* ── The search itself: metro pills + vibe textbox ── */}
          <div id="start-searching" className="w-full flex flex-col items-center gap-3 mt-2 scroll-mt-24">
            <MetroPills />
            <div
              className="w-full max-w-2xl flex flex-col gap-2 rounded-2xl p-4 pb-3"
              style={{
                background: 'rgba(16,16,24,0.85)',
                border: '1px solid rgba(255,200,60,0.5)',
                boxShadow: '0 0 48px -6px var(--ts-accent-glow), 0 8px 30px rgba(10,10,16,0.5)',
                backdropFilter: 'blur(8px)',
              }}
              data-testid="home-vibe-box"
            >
              <textarea
                value={vibeText}
                onChange={(e) => setVibeText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submitVibe();
                  }
                }}
                rows={4}
                placeholder={`Tell us your budget, the vibe and location you want, and how many bedrooms…\n\n${HERO_EXAMPLES[selectedMetro || ''] || HERO_EXAMPLES.Austin}`}
                aria-label="Tell us your budget, location vibe, and bedroom count"
                className="w-full bg-transparent resize-none focus:outline-none text-base md:text-lg min-h-[170px] sm:min-h-[130px] md:min-h-[112px]"
                style={{ color: '#fff', fontFamily: 'var(--ts-font-body)', lineHeight: 1.5 }}
                data-testid="home-vibe-input"
              />
              <div className="flex items-center justify-between gap-3">
                <span
                  className="uppercase hidden sm:block"
                  style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(255,210,77,0.75)' }}
                >
                  Budget · Location · Bedrooms · Vibe
                </span>
                <button
                  onClick={submitVibe}
                  className="ml-auto inline-flex items-center gap-2 rounded-xl px-5 h-11 font-bold transition-all hover:opacity-90 active:scale-95"
                  style={{
                    background: 'var(--ts-accent-primary)',
                    color: 'var(--ts-accent-contrast, #141821)',
                    fontFamily: 'var(--ts-font-display)',
                    fontSize: 15,
                    boxShadow: '0 0 22px -4px var(--ts-accent-glow)',
                  }}
                  data-testid="home-vibe-send"
                >
                  Find my fit <ArrowRight size={16} />
                </button>
              </div>
            </div>
            {/* T11 — on mobile the demoted text links below are easy to miss
                and the bottom nav no longer carries a Criteria tab, so
                criteria search gets one visible full-width tap. */}
            <button
              type="button"
              onClick={() => goDemoted('/search')}
              className="sm:hidden w-full max-w-2xl rounded-xl font-semibold text-sm transition-all active:scale-[0.99]"
              style={{
                minHeight: 48,
                background: 'transparent',
                border: '1px solid rgba(255, 210, 77, 0.5)',
                color: 'var(--ts-accent-text)',
              }}
              data-testid="home-mode-criteria-mobile"
            >
              Search by criteria instead
            </button>

            {/* Demoted modes — ADR-0001: expressive shortcuts, not separate engines */}
            <div
              className="hidden sm:inline-flex items-center gap-2 text-sm flex-wrap justify-center rounded-full px-4 py-2"
              style={{
                color: 'rgba(255,255,255,0.78)',
                background: 'rgba(10,10,16,0.88)',
                border: '1px solid rgba(255,255,255,0.10)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <span>Know exactly what you want?</span>
              <button
                onClick={() => goDemoted('/search')}
                className="underline underline-offset-2 font-semibold transition-opacity hover:opacity-80"
                style={{ color: 'rgba(255,255,255,0.9)' }}
                data-testid="home-mode-criteria"
              >
                Search by criteria
              </button>
              <span aria-hidden style={{ color: 'rgba(255,255,255,0.4)' }}>·</span>
              <button
                onClick={() => goDemoted('/browse')}
                className="underline underline-offset-2 font-semibold transition-opacity hover:opacity-80"
                style={{ color: 'rgba(255,255,255,0.9)' }}
                data-testid="home-mode-map"
              >
                Browse the map
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works / why it's free ──
          Tight seam against the hero (gold hairline, reduced padding — the
          old py-20 + hero fade left a ~200px dead black band), HUD kicker,
          and the three points rendered as the actual three-phase locating
          flow in board-material cards. The "Start searching" pill that
          scrolled back up was redundant with the hero box and removed. */}
      <section className="relative overflow-hidden px-6 pt-12 pb-14 sm:pt-14 sm:pb-16 flex flex-col items-center gap-8" style={{ background: 'var(--ts-anchor)', borderTop: '1px solid rgba(255,200,60,0.14)' }}>
        <div aria-hidden className="absolute -top-24 left-1/2 -translate-x-1/2 w-[700px] max-w-full h-56 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(251,191,18,0.09) 0%, rgba(251,191,18,0) 70%)' }} />
        <div className="relative flex flex-col items-center gap-3 text-center max-w-2xl">
          <span className="ts-hud-label">How it works · 100% free</span>
          <h2 style={{ font: '800 clamp(24px, 4.2vw, 34px) var(--ts-font-display)', letterSpacing: '-0.03em', color: '#fff', lineHeight: 1.1, margin: 0 }}>
            Free, because apartments pay us.
          </h2>
          <p className="text-sm sm:text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)', maxWidth: '38rem' }}>
            List “Taco Street Locating” as your referral when you tour — that’s the whole deal.
          </p>
        </div>
        <div className="relative grid gap-4 w-full max-w-4xl sm:grid-cols-3">
          <StepCard step="01" icon="search" title="Search" body="4,500+ Texas apartments — AI search backed by local human knowledge." />
          <StepCard step="02" emoji="🌮" title="Save your favorites" body="Build a shortlist of the buildings that actually fit your life." />
          <StepCard step="03" icon="file-text" title="Get real pricing" body="We pull exact pricing + availability for your list, within 24 hours." />
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="px-6 sm:px-16 py-12" style={{ background: '#0F131A', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="max-w-6xl mx-auto flex flex-col gap-8">
          <div className="flex flex-col gap-8 sm:flex-row sm:justify-between">
            <div className="flex flex-col gap-2.5 max-w-xs">
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl">🌮</span>
                <span style={{ font: '800 16px var(--ts-font-display)', letterSpacing: '-0.02em', color: '#fff' }}>Taco Street Locating</span>
                <span className="ts-ai-badge ts-ai-badge--on-dark" aria-label="AI" style={{ fontSize: 12 }}>AI</span>
              </div>
              <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Human + AI + taco powered apartment locating across Texas. Free, always. The apartments pay us.
              </p>
            </div>
            <div className="flex gap-16">
              <FooterCol heading="Search" links={[['Criteria Search', '/search'], ['Vibe Search', '/vibe'], ['Map Search', '/browse'], ['Saved', '/my-list']]} />
              <FooterCol heading="Account" links={[['Sign In', '/login'], ['Dashboard', '/dashboard']]} />
              <div className="flex flex-col gap-3">
                <span className="text-[11px] font-bold tracking-[0.08em] uppercase" style={{ color: 'var(--ts-text-tertiary)' }}>Humans</span>
                <a href={getIntakeUrl(selectedMetro)} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline inline-flex items-center min-h-[44px] sm:min-h-0" style={{ color: 'rgba(255,255,255,0.7)' }} data-testid="footer-contact">
                  Talk to a human
                </a>
                <a href="mailto:alex@tacostreetlocating.com" className="text-sm underline min-h-[44px] sm:min-h-0" style={{ color: 'var(--ts-text-secondary)' }}>Prefer email? Contact Alex</a>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-between gap-1 pt-5 pb-14 md:pb-0" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <span className="text-xs" style={{ color: 'var(--ts-text-tertiary)' }}>© 2026 Taco Street Locating · Licensed Texas locator</span>
            <span className="text-xs" style={{ color: 'var(--ts-text-tertiary)' }}>Made with 🌮 in Austin, TX</span>
          </div>
        </div>
      </footer>

      <MobileBottomNav currentPage="home" />
    </div>
  );
}

/* DESIGN.md requires >= 44px touch targets on mobile. These were 20px — the
   smallest tappable things on the site. `gap-3` plus a 20px line box gave the
   right optical rhythm, so the height comes from the link itself and the gap
   shrinks to keep the column looking the same. */
function FooterCol({ heading, links }: { heading: string; links: [string, string][] }) {
  return (
    <div className="flex flex-col gap-3 sm:gap-3">
      <span className="text-[11px] font-bold tracking-[0.08em] uppercase" style={{ color: 'var(--ts-text-tertiary)' }}>{heading}</span>
      <div className="flex flex-col gap-0 sm:gap-3">
        {links.map(([label, to]) => (
          <Link
            key={label}
            to={to}
            className="text-sm hover:underline inline-flex items-center min-h-[44px] sm:min-h-0"
            style={{ color: 'rgba(255,255,255,0.7)' }}
          >
            {label}
          </Link>
        ))}
      </div>
    </div>
  );
}
